/* =========================================================================
   Parts4Cars Theme — JavaScript
   - Vanilla ES module, web components, Cart AJAX API, predictive search
   ========================================================================= */

const routes = (window.Shopify && window.Shopify.routes) || {};
const strings = (window.Shopify && window.Shopify.strings) || {};

/* ---------- Utilities ---------- */

const fetchConfig = (type = 'json') => ({
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/* P4C: stejné pravidlo jako snippets/p4c-money.liquid — cena z JS musí vypadat jako cena z Liquidu.
   Formát bere z obchodu (window.Shopify.currency.format), u CZK navíc „7 786 Kč“: bez ,00 a s nezalomitelnou mezerou. */
function formatMoney(cents, format = (window.Shopify && window.Shopify.currency && window.Shopify.currency.format) || '{{amount}}') {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = Math.round(Number(cents)) || 0;
  const activeCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
  const num = (value, decimals, thousands, decimal) => {
    const fixed = (Math.abs(value) / 100).toFixed(decimals).split('.');
    const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return (value < 0 ? '-' : '') + whole + (fixed[1] ? decimal + fixed[1] : '');
  };
  const match = String(format).match(/\{\{\s*(\w+)\s*\}\}/);
  const key = match ? match[1] : 'amount';
  let amount;
  switch (key) {
    case 'amount_no_decimals': amount = num(cents, 0, ',', '.'); break;
    case 'amount_with_comma_separator': amount = num(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': amount = num(cents, 0, '.', ','); break;
    case 'amount_with_space_separator': amount = num(cents, 2, ' ', ','); break;
    case 'amount_no_decimals_with_space_separator': amount = num(cents, 0, ' ', ','); break;
    case 'amount_with_period_and_space_separator': amount = num(cents, 2, ' ', '.'); break;
    case 'amount_with_apostrophe_separator': amount = num(cents, 2, "'", '.'); break;
    default: amount = num(cents, 2, ',', '.');
  }
  let out = match ? String(format).replace(match[0], amount) : amount;
  if (activeCurrency === 'CZK') {
    // = money_without_trailing_zeros | replace: '.', NBSP | replace: ' Kč', NBSP + 'Kč'
    out = out.replace(/,00(?!\d)/, '').replace(/\./g, ' ').replace(' Kč', ' Kč');
  }
  return out;
}

function debounce(fn, wait = 250) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(null, args), wait);
  };
}

function throttle(fn, wait = 200) {
  let last = 0;
  let timeout;
  return (...args) => {
    const now = Date.now();
    if (now - last >= wait) {
      last = now;
      fn.apply(null, args);
    } else {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        last = Date.now();
        fn.apply(null, args);
      }, wait - (now - last));
    }
  };
}

function publish(eventName, detail = {}) {
  document.dispatchEvent(new CustomEvent(eventName, { detail }));
}

function on(eventName, handler) {
  document.addEventListener(eventName, handler);
}

function trapFocus(container, focusStart) {
  if (!container) return () => {};
  const focusable = container.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])');
  if (!focusable.length) return () => {};
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  (focusStart || first).focus();

  function onKey(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  container.addEventListener('keydown', onKey);
  return () => container.removeEventListener('keydown', onKey);
}

function showToast(message, type = 'default', duration = 3500) {
  const container = document.getElementById('ToastContainer');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.setAttribute('role', 'status');
  toast.textContent = message;
  container.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('is-visible'));
  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

const bodyLocks = new Set();

function lockBody(source) {
  bodyLocks.add(source);
  document.body.classList.add('is-drawer-open');
  document.body.style.overflow = 'hidden';
}

function unlockBody(source) {
  bodyLocks.delete(source);
  if (bodyLocks.size === 0) {
    document.body.classList.remove('is-drawer-open');
    document.body.style.overflow = '';
  }
}

/* ---------- Cart API ---------- */

class CartAPI {
  static async get() {
    const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  static async add(items) {
    const body = Array.isArray(items) ? { items } : { items: [items] };
    const res = await fetch(routes.cart_add_url, { ...fetchConfig(), body: JSON.stringify(body) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const error = new Error(err.description || err.message || strings.cartError || 'Cart error');
      error.data = err;
      throw error;
    }
    const data = await res.json();
    await CartAPI.refreshState();
    return data;
  }

  static async change(payload) {
    const res = await fetch(routes.cart_change_url, { ...fetchConfig(), body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(strings.cartError || 'Cart error');
    const data = await res.json();
    await CartAPI.refreshState(data);
    return data;
  }

  static async update(updates) {
    const res = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ updates }) });
    if (!res.ok) throw new Error(strings.cartError || 'Cart error');
    const data = await res.json();
    await CartAPI.refreshState(data);
    return data;
  }

  static async clear() {
    const res = await fetch(routes.cart_clear_url, fetchConfig());
    const data = await res.json();
    await CartAPI.refreshState(data);
    return data;
  }

  static async refreshState(cart = null) {
    const data = cart || (await CartAPI.get());
    publish('cart:updated', data);
    return data;
  }

  static async updateNote(note) {
    const res = await fetch(`${routes.cart_url}/update.js`, { ...fetchConfig(), body: JSON.stringify({ note }) });
    return res.json();
  }
}

/* ---------- Cart count listener ---------- */

on('cart:updated', (e) => {
  const cart = e.detail || {};
  const count = cart.item_count ?? 0;
  document.querySelectorAll('.js-cart-count, [data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.classList.toggle('is-empty', count === 0);
    el.classList.remove('is-bump');
    void el.offsetWidth;
    el.classList.add('is-bump');
  });
});

document.addEventListener('click', async (e) => {
  const quickAdd = e.target.closest('[data-quick-add]');
  if (!quickAdd) return;

  e.preventDefault();
  e.stopPropagation();

  const variantId = quickAdd.dataset.variantId;
  if (!variantId || quickAdd.getAttribute('aria-disabled') === 'true') return;

  quickAdd.classList.add('is-loading');
  quickAdd.setAttribute('aria-disabled', 'true');

  try {
    await CartAPI.add({ id: Number(variantId), quantity: 1 });
    showToast(strings.addedToCart || 'Přidáno do košíku', 'success');
    publish('cart:open');
  } catch (err) {
    showToast(err.message || strings.cartError || 'Produkt se nepodařilo přidat', 'error');
  } finally {
    quickAdd.classList.remove('is-loading');
    quickAdd.removeAttribute('aria-disabled');
  }
});

/* ---------- Cart Drawer web component ---------- */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.releaseFocus = null;
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');

    this.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        if (window.Shopify?.cartType === 'page') return;
        e.preventDefault();
        this.open();
      });
    });

    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-quantity]');
      if (input) {
        const wrapper = input.closest('[data-key]');
        this.updateLine(wrapper.dataset.key, parseInt(input.value, 10));
      }
    });

    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove]');
      if (removeBtn) {
        e.preventDefault();
        this.updateLine(removeBtn.dataset.key, 0);
      }
    });

    const noteInput = this.querySelector('[data-cart-note]');
    if (noteInput) {
      noteInput.addEventListener('change', debounce(() => CartAPI.updateNote(noteInput.value), 400));
    }

    on('cart:updated', (e) => this.render(e.detail));
    on('cart:open', () => this.open());
  }

  open() {
    document.querySelector('mobile-menu')?.close();
    document.querySelector('collection-filters-drawer')?.close();
    this.setAttribute('aria-hidden', 'false');
    lockBody('cart-drawer');
    this.releaseFocus = trapFocus(this.querySelector('.cart-drawer__panel'));
    document.addEventListener('keydown', this.onKeyDown);
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    unlockBody('cart-drawer');
    if (this.releaseFocus) this.releaseFocus();
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown(e) {
    if (e.key === 'Escape') this.close();
  }

  async updateLine(key, quantity) {
    try {
      const cart = await CartAPI.change({ id: key, quantity });
      if (cart.status && cart.status >= 400) throw new Error(cart.description);
    } catch (err) {
      showToast(err.message || strings.cartError, 'error');
    }
  }

  async render(cart) {
    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (res.ok) {
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newDrawer = doc.getElementById('CartDrawer');
        if (newDrawer) {
          this.innerHTML = newDrawer.innerHTML;
          /* skripty vložené přes innerHTML se nespustí → znovu je vytvořit (doprava, PPL, souhlasy; každý má vlastní pojistku proti dvojímu běhu) */
          this.querySelectorAll('script').forEach((old) => {
            const s = document.createElement('script');
            Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
            s.textContent = old.textContent;
            old.replaceWith(s);
          });
          this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        }
      }
    } catch {
      /* non-critical */
    }
  }

}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Product Form web component ---------- */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    const submitBtn = this.form.querySelector('[type="submit"]');
    if (!submitBtn || submitBtn.getAttribute('aria-disabled') === 'true') return;

    submitBtn.classList.add('is-loading');
    submitBtn.setAttribute('aria-disabled', 'true');

    try {
      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');
      const res = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.description || data.message || strings.cartError, 'error');
      } else {
        showToast(strings.addedToCart || 'Přidáno do košíku', 'success');
        await CartAPI.refreshState();
        publish('cart:open');
      }
    } catch (err) {
      showToast(err.message || strings.cartError, 'error');
    } finally {
      submitBtn.classList.remove('is-loading');
      submitBtn.removeAttribute('aria-disabled');
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Variant Selector web component ---------- */

class VariantSelector extends HTMLElement {
  connectedCallback() {
    const script = this.querySelector('[data-variants]');
    this.variants = script ? JSON.parse(script.textContent) : [];
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;

    this.addEventListener('change', () => this.onChange());
  }

  onChange() {
    const options = this.getSelectedOptions();
    const variant = this.variants.find((v) => v.options.every((opt, idx) => opt === options[idx]));
    if (!variant) {
      this.toggleUnavailable();
      return;
    }
    this.currentVariant = variant;
    this.updateUrl(variant);
    this.updateInputs(variant);
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateMedia(variant);
    publish('variant:change', { variant });
  }

  getSelectedOptions() {
    const selectors = this.querySelectorAll('fieldset');
    return Array.from(selectors).map((fieldset) => {
      const selectEl = fieldset.querySelector('[data-option-select]');
      if (selectEl) return selectEl.value;
      const checked = fieldset.querySelector('[data-option-input]:checked');
      return checked ? checked.value : null;
    });
  }

  updateUrl(variant) {
    if (!this.productUrl) return;
    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateInputs(variant) {
    document.querySelectorAll(`[data-variant-id]`).forEach((el) => {
      el.value = variant.id;
    });
    this.querySelectorAll('[data-selected-value]').forEach((span, idx) => {
      span.textContent = variant.options[idx] || '';
    });
  }

  updatePrice(variant) {
    document.querySelectorAll(`.main-product__price .price__current`).forEach((el) => {
      el.textContent = formatMoney(variant.price);
    });
    document.querySelectorAll('.main-product__price .price__compare').forEach((el) => {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        el.textContent = formatMoney(variant.compare_at_price);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
  }

  updateAvailability(variant) {
    const btn = document.querySelector('.product-form__submit');
    if (!btn) return;
    const textSpan = btn.querySelector('.btn__text');
    if (variant.available) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      if (textSpan) textSpan.textContent = strings.addToCart || 'Přidat do košíku';
    } else {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      if (textSpan) textSpan.textContent = strings.soldOut || 'Vyprodáno';
    }
  }

  updateMedia(variant) {
    if (!variant.featured_media) return;
    const mediaId = variant.featured_media.id;
    const gallery = document.querySelector('product-gallery');
    if (gallery) gallery.setActive(mediaId);
  }

  toggleUnavailable() {
    const btn = document.querySelector('.product-form__submit');
    if (!btn) return;
    btn.disabled = true;
    const textSpan = btn.querySelector('.btn__text');
    if (textSpan) textSpan.textContent = strings.unavailable || 'Nedostupné';
  }
}
customElements.define('variant-selector', VariantSelector);

/* ---------- Quantity input web component ---------- */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.buttons = this.querySelectorAll('button');
    this.buttons.forEach((btn) => btn.addEventListener('click', (e) => this.onClick(e)));
    this.input?.addEventListener('change', () => this.validate());
  }

  onClick(e) {
    e.preventDefault();
    if (!this.input) return;
    const action = e.currentTarget.dataset.action;
    const min = parseInt(this.input.min, 10) || 0;
    const current = parseInt(this.input.value, 10) || 0;
    if (action === 'increase') this.input.value = current + 1;
    else if (action === 'decrease') this.input.value = Math.max(min, current - 1);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  validate() {
    const min = parseInt(this.input.min, 10) || 0;
    const val = parseInt(this.input.value, 10);
    if (isNaN(val) || val < min) this.input.value = min;
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Product Gallery web component ---------- */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = this.querySelectorAll('[data-gallery-slide]');
    this.thumbs = this.querySelectorAll('[data-gallery-thumb]');
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.setActive(thumb.dataset.mediaId));
    });
    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.step(-1);
      if (e.key === 'ArrowRight') this.step(1);
    });
  }

  setActive(mediaId) {
    this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId == mediaId));
    this.thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId == mediaId));
  }

  step(dir) {
    const activeIdx = Array.from(this.slides).findIndex((s) => s.classList.contains('is-active'));
    const next = (activeIdx + dir + this.slides.length) % this.slides.length;
    this.setActive(this.slides[next].dataset.mediaId);
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Predictive Search web component ---------- */

/* Tolerantní vyhledávání: čeština skloňuje („volanty“ vs „volant“) a Shopify
   prefixuje jen poslední slovo dotazu. Když dotaz nevrátí produkty, zkusíme
   postupně varianty: oseknuté koncovky a přehozené pořadí slov. */
function stemCzechWord(word) {
  if (word.length <= 4 || /\d/.test(word)) return word;
  const suffixes = ['ovými', 'ovým', 'ových', 'ové', 'ová', 'ový', 'ami', 'ách', 'emi', 'ech', 'ům', 'y', 'e', 'i', 'u', 'a', 'é', 'á', 'ý', 'í', 'ů'];
  const lower = word.toLowerCase();
  for (const suffix of suffixes) {
    if (lower.length - suffix.length >= 4 && lower.endsWith(suffix)) {
      return word.slice(0, word.length - suffix.length);
    }
  }
  return word;
}

function buildSearchVariants(q) {
  const words = q.split(/\s+/).filter(Boolean);
  const variants = [q];
  const push = (v) => {
    if (v && !variants.includes(v)) variants.push(v);
  };
  const stemmed = words.map(stemCzechWord);
  push(stemmed.join(' '));
  if (words.length > 1) {
    push(words.slice().reverse().join(' '));
    push(stemmed.slice().reverse().join(' '));
  }
  return variants.slice(0, 4);
}

function countPredictiveProducts(html) {
  return (html.match(/predictive-search__item-img/g) || []).length;
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (!routes.predictive_search_url) return;
    this.input = this.querySelector('[data-search-input], [data-predictive-input]');
    this.results = this.querySelector('[data-search-results], [data-predictive-results]');
    this.content = this.querySelector('[data-search-content], [data-predictive-content]');
    this.loading = this.querySelector('[data-search-loading], [data-predictive-loading]');
    this.clearBtn = this.querySelector('[data-search-clear], [data-predictive-clear]');

    if (!this.input || !this.results || !this.content) return;

    this.input.addEventListener('input', debounce(() => this.search(), 220));
    this.input.addEventListener('focus', () => this.open());
    this.clearBtn?.addEventListener('click', () => this.clear());
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });
    this.input.addEventListener('keydown', (e) => this.handleKey(e));
  }

  async search() {
    const q = this.input.value.trim();
    this.classList.toggle('has-value', q.length > 0);
    if (q.length < 2) {
      this.close();
      return;
    }
    this.open();
    if (this.loading) this.loading.hidden = false;
    const token = (this.searchToken = (this.searchToken || 0) + 1);

    try {
      let best = null;
      for (const variant of buildSearchVariants(q)) {
        const text = await this.fetchPredictive(variant);
        if (token !== this.searchToken) return;
        const productCount = countPredictiveProducts(text);
        if (best === null) best = text;
        if (productCount > 0) {
          best = text;
          break;
        }
      }
      if (token !== this.searchToken) return;
      this.renderFromHtml(best || '');
    } catch {
      if (token !== this.searchToken) return;
      this.content.innerHTML = `<p class="predictive-search__empty">${strings.searchError || 'Chyba při vyhledávání'}</p>`;
    } finally {
      if (token === this.searchToken && this.loading) this.loading.hidden = true;
    }
  }

  async fetchPredictive(q) {
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,article,collection,page',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      'resources[options][prefix]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku,tag',
      section_id: 'predictive-search',
    });
    const res = await fetch(`${routes.predictive_search_url}?${params}`);
    return res.text();
  }

  renderFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('[data-section-type="predictive-search"]') || doc.querySelector('predictive-search [data-search-content]');
    if (section) {
      this.content.innerHTML = section.innerHTML;
    } else {
      this.buildGenericResults(html);
    }
  }

  buildGenericResults() {
    this.content.innerHTML = `<p class="predictive-search__empty">${strings.searchEmpty || 'Nic jsme nenašli. Zkuste OEM číslo nebo značku.'}</p>`;
  }

  open() {
    if (this.results) this.results.hidden = false;
  }

  close() {
    if (this.results) this.results.hidden = true;
  }

  clear() {
    this.input.value = '';
    this.classList.remove('has-value');
    this.close();
    this.input.focus();
  }

  handleKey(e) {
    if (e.key === 'Escape') this.clear();
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Mobile Menu web component ---------- */

class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
    this.release = null;
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
    this.toggles = document.querySelectorAll('[data-menu-toggle]');

    this.toggles.forEach((btn) => btn.addEventListener('click', () => this.open()));

    this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
  }

  open() {
    document.getElementById('CartDrawer')?.close();
    document.querySelector('collection-filters-drawer')?.close();
    this.setAttribute('aria-hidden', 'false');
    this.toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    lockBody('mobile-menu');
    document.addEventListener('keydown', this.onKey);
    this.release = trapFocus(this.querySelector('.mobile-menu__panel'));
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    unlockBody('mobile-menu');
    document.removeEventListener('keydown', this.onKey);
    if (this.release) this.release();
  }
}
customElements.define('mobile-menu', MobileMenu);

/* ---------- Collection filters drawer ---------- */

class CollectionFiltersDrawer extends HTMLElement {
  connectedCallback() {
    this.release = null;
    this.media = window.matchMedia('(max-width: 959.98px)');
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
    this.onMediaChange = () => this.syncMode();
    this.onDocumentClick = (e) => {
      const toggle = e.target.closest('[data-filters-toggle]');
      if (toggle && toggle.getAttribute('aria-controls') === this.id) {
        e.preventDefault();
        this.open();
        return;
      }

      if (e.target.closest('[data-filters-close]')) {
        e.preventDefault();
        this.close();
      }
    };
    this.syncMode();
    this.media.addEventListener?.('change', this.onMediaChange);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKey);
    this.media?.removeEventListener?.('change', this.onMediaChange);
    unlockBody('collection-filters');
  }

  syncMode() {
    const panel = this.querySelector('.main-collection__filters');
    if (this.media.matches) {
      if (!this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
    } else {
      this.setAttribute('aria-hidden', 'false');
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      unlockBody('collection-filters');
      document.removeEventListener('keydown', this.onKey);
      document.querySelectorAll(`[data-filters-toggle][aria-controls="${this.id}"]`).forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }
  }

  open() {
    if (!this.media.matches) return;
    document.getElementById('CartDrawer')?.close();
    document.querySelector('mobile-menu')?.close();
    this.setAttribute('aria-hidden', 'false');
    document.querySelectorAll(`[data-filters-toggle][aria-controls="${this.id}"]`).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'true');
    });
    lockBody('collection-filters');
    document.addEventListener('keydown', this.onKey);
    this.release = trapFocus(this.querySelector('.main-collection__filters'));
  }

  close() {
    this.setAttribute('aria-hidden', this.media.matches ? 'true' : 'false');
    document.querySelectorAll(`[data-filters-toggle][aria-controls="${this.id}"]`).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
    unlockBody('collection-filters');
    document.removeEventListener('keydown', this.onKey);
    if (this.release) this.release();
  }
}

customElements.define('collection-filters-drawer', CollectionFiltersDrawer);

/* ---------- Facet Filters web component ---------- */

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.abortController = null;
    const submit = debounce(() => this.apply(), 300);
    this.form.addEventListener('input', submit);
    this.form.addEventListener('change', submit);
  }

  async apply() {
    const params = new URLSearchParams(new FormData(this.form));
    const sort = document.getElementById('SortBy');
    if (sort && sort.value) params.set('sort_by', sort.value);
    const url = `${window.location.pathname}?${params.toString()}`;
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    document.querySelector('.main-collection__results')?.classList.add('is-loading');
    try {
      const res = await fetch(url, { signal: this.abortController.signal });
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newResults = doc.querySelector('.main-collection__results');
      const currentResults = document.querySelector('.main-collection__results');
      const newFilters = doc.querySelector('.main-collection__filters');
      const currentFilters = document.querySelector('.main-collection__filters');
      if (newResults && currentResults) currentResults.innerHTML = newResults.innerHTML;
      if (newFilters && currentFilters) currentFilters.innerHTML = newFilters.innerHTML;
      window.history.pushState({}, '', url);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      document.querySelector('.main-collection__results')?.classList.remove('is-loading');
    }
  }
}
customElements.define('facet-filters', FacetFilters);

/* ---------- Facet Sort (standalone select) ---------- */

class FacetSort extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.select?.addEventListener('change', () => {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', this.select.value);
      window.location.search = params.toString();
    });
  }
}
customElements.define('facet-sort', FacetSort);

/* ---------- Announcement carousel ---------- */

class AnnouncementCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.autoplay !== 'true') return;
    this.interval = (parseInt(this.dataset.interval, 10) || 5) * 1000;
    this.track = this.querySelector('[data-track]');
    this.slides = this.querySelectorAll('.announcement-bar__slide');
    if (this.slides.length < 2) return;
    this.index = 0;
    this.start();
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stop() { if (this.timer) clearInterval(this.timer); }

  next() {
    this.index = (this.index + 1) % this.slides.length;
    if (this.track) this.track.style.transform = `translateX(-${this.index * 100}%)`;
  }
}
customElements.define('announcement-carousel', AnnouncementCarousel);

/* ---------- Product Recommendations ---------- */

class ProductRecommendations extends HTMLElement {
  async connectedCallback() {
    if (!this.dataset.url) return;
    try {
      const res = await fetch(this.dataset.url);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('product-recommendations');
      if (fresh && fresh.innerHTML.trim()) {
        this.innerHTML = fresh.innerHTML;
      } else {
        this.remove();
      }
    } catch { /* silent */ }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- Cart Page ---------- */

class CartPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-quantity]');
      if (!input) return;
      const row = input.closest('[data-key]');
      this.updateLine(row.dataset.key, parseInt(input.value, 10));
    });

    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove]');
      if (removeBtn) {
        e.preventDefault();
        this.updateLine(removeBtn.dataset.key, 0);
      }
    });

    const noteInput = this.querySelector('[data-cart-note]');
    if (noteInput) {
      noteInput.addEventListener('change', debounce(() => CartAPI.updateNote(noteInput.value), 400));
    }
  }

  async updateLine(key, quantity) {
    try {
      const cart = await CartAPI.change({ id: key, quantity });
      if (quantity === 0) {
        const row = this.querySelector(`[data-key="${key}"]`);
        if (row) row.remove();
      }
      const subtotal = this.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      (cart.items || []).forEach((item) => {
        const r = this.querySelector(`[data-key="${item.key}"] .cart-row__total`);
        if (r) r.textContent = formatMoney(item.final_line_price);
      });
      if (cart.item_count === 0) window.location.reload();
    } catch (err) {
      showToast(err.message || strings.cartError, 'error');
    }
  }
}
customElements.define('cart-page', CartPage);

/* ---------- Site header (sticky) ---------- */

class SiteHeader extends HTMLElement {
  connectedCallback() {
    if (!this.classList.contains('site-header--sticky')) return;
    let lastY = window.scrollY;
    const onScroll = throttle(() => {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 10);
      this.classList.toggle('is-hidden', y > lastY && y > 200);
      lastY = y;
    }, 120);
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}
customElements.define('site-header', SiteHeader);

/* ---------- Theme motion ---------- */

function initThemeMotion() {
  if (document.body) document.body.classList.add('is-ready');
  document.querySelectorAll('.hero__content').forEach((el) => el.classList.add('is-animated'));

  const progress = document.querySelector('.scroll-progress');
  if (progress) {
    const updateProgress = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 0);
      const ratio = scrollable > 0 ? Math.min(Math.max(window.scrollY / scrollable, 0), 1) : 0;
      progress.style.transform = `scaleX(${ratio})`;
    };
    updateProgress();
    window.addEventListener('scroll', throttle(updateProgress, 16), { passive: true });
    window.addEventListener('resize', throttle(updateProgress, 80));
  }

  const observed = new Set();
  const selectors = {
    reveal: [
      '.marquee-section',
      '.hero-search__inner',
      '.icon-categories__header',
      '.featured-products-safe__header',
      '.reco-band__header',
      '.search-showcase__header',
      '.testimonials__header',
      '.trust-badges__header',
      '.faq__header',
      '.newsletter__inner',
      '.main-collection__header',
      '.main-page__header',
      '.kontakt__head',
      '.kontakt__map-block',
      '.related-products__header',
    ],
    stagger: [
      '.icon-categories__grid',
      '.usp-bar__list',
      '.featured-products-safe__grid',
      '.reco-band__cards',
      '.search-showcase__grid',
      '.testimonials__grid',
      '.trust-badges__grid',
      '.faq__list',
      '.site-footer__grid',
      '.kontakt__grid',
      '.main-page--faq .main-page__content',
    ],
  };

  selectors.reveal.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      el.classList.add('reveal');
      observed.add(el);
    });
  });

  selectors.stagger.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      el.classList.add('reveal-stagger');
      observed.add(el);
    });
  });

  if (!observed.size) return;

  const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    observed.forEach((el) => el.classList.add('in'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('in');
      observer.unobserve(entry.target);
    });
  }, {
    threshold: 0.14,
    rootMargin: '0px 0px -12% 0px',
  });

  observed.forEach((el) => observer.observe(el));

  // Full-page renders or aggressive lazy paints can keep below-the-fold blocks hidden.
  // Reveal any remaining sections after the first motion pass so the homepage never looks empty.
  window.setTimeout(() => {
    observed.forEach((el) => {
      if (!el.classList.contains('in')) {
        el.classList.add('in');
        observer.unobserve(el);
      }
    });
  }, 1400);
}

/* ---------- Share snippet interactivity ---------- */

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-share-btn]');
  if (btn) {
    const share = btn.closest('.share');
    const menu = share.querySelector('.share__menu');
    const data = { title: share.dataset.title, url: share.dataset.url };
    if (navigator.share) {
      navigator.share(data).catch(() => {});
    } else if (menu) {
      menu.hidden = !menu.hidden;
    }
  }

  const copyBtn = e.target.closest('[data-share-copy]');
  if (copyBtn) {
    navigator.clipboard.writeText(copyBtn.closest('.share').dataset.url).then(() => {
      const shareStrings = (window.Shopify && window.Shopify.strings) || {};
      copyBtn.textContent = shareStrings.copied || '✓ Zkopírováno';
      setTimeout(() => { copyBtn.textContent = shareStrings.copyLink || 'Kopírovat odkaz'; }, 1800);
    });
  }

  const authToggle = e.target.closest('[data-auth-toggle]');
  if (authToggle) {
    const target = authToggle.dataset.authToggle;
    document.getElementById('login').hidden = target !== 'login';
    document.getElementById('recover').hidden = target !== 'recover';
  }
});

/* ---------- Compatibility search filter ---------- */

document.addEventListener('input', (e) => {
  const input = e.target.closest('[data-compat-search]');
  if (!input) return;
  const term = input.value.trim().toLowerCase();
  const list = input.closest('.main-product__compatibility-content').querySelector('[data-compat-list]');
  list.querySelectorAll('.compat-list__item').forEach((li) => {
    const text = li.textContent.trim().toLowerCase();
    li.hidden = term && !text.includes(term);
  });
});

/* ---------- Initial boot ---------- */

function initThemeSwitcher() {
  const storageKey = 'p4c-color-mode';
  const switchers = document.querySelectorAll('[data-theme-switcher]');
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  const validModes = new Set(['light', 'dark']);

  const getStoredMode = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      return validModes.has(stored) ? stored : null;
    } catch {
      return null;
    }
  };

  const getCurrentMode = () => (
    getStoredMode()
    || document.documentElement.getAttribute('data-mode')
    || document.body.dataset.mode
    || 'light'
  );

  const applyMode = (mode, persist = true) => {
    const normalized = validModes.has(mode) ? mode : 'light';
    document.documentElement.setAttribute('data-mode', normalized);
    document.body.dataset.mode = normalized;
    if (metaTheme) metaTheme.setAttribute('content', normalized === 'light' ? '#F5F6F7' : '#0A0D10');

    switchers.forEach((switcher) => {
      switcher.querySelectorAll('[data-theme-mode]').forEach((button) => {
        const active = button.dataset.themeMode === normalized;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    });

    if (persist) {
      try {
        localStorage.setItem(storageKey, normalized);
      } catch {
        // Storage can be disabled in private browsing.
      }
    }
  };

  // P4C: obchod běží výhradně ve světlém režimu. Vynuť light při každém načtení
  // (přebije i případně uložený 'dark') a přepínač už není v DOM.
  applyMode('light', true);
}

// Clean up empty cart count badge if count is 0
document.querySelectorAll('.js-cart-count').forEach((el) => {
  if (!el.textContent.trim() || el.textContent.trim() === '0') el.classList.add('is-empty');
});

// Hero smart-search tab switching
document.addEventListener('DOMContentLoaded', () => {
  initThemeSwitcher();
  initThemeMotion();
  document.querySelectorAll('[data-hero-search]').forEach((card) => {
    const tabs = card.querySelectorAll('.search-tabs button');
    const panels = card.querySelectorAll('.search-panel');
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        tabs.forEach((t) => {
          t.classList.toggle('active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        panels.forEach((p) => {
          p.classList.toggle('is-active', p.dataset.panel === target);
        });
      });
    });
    card.querySelectorAll('.quick-chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        const panel = chip.closest('.search-panel');
        const input = panel && panel.querySelector('input[type="text"]');
        if (input) { input.value = chip.dataset.quick || chip.textContent.trim(); input.focus(); }
      });
    });

    // Reálné hledání: brand + model + motor + rok se složí do jednoho q
    // (Shopify search čte jen q). Pomocné selecty se při odeslání vypnou,
    // aby se neposílaly ignorované parametry. Žádné dekorativní pole.
    card.querySelectorAll('form.search-panel').forEach((form) => {
      form.addEventListener('submit', (e) => {
        const qInput = form.querySelector('input[name="q"]');
        const brand = form.querySelector('select[name="brand"]');
        const year = form.querySelector('select[name="year"]');
        const engine = form.querySelector('select[name="engine"]');
        const terms = [];
        if (brand && brand.value) terms.push(brand.value);
        if (qInput && qInput.value.trim()) terms.push(qInput.value.trim());
        if (engine && engine.value) terms.push(engine.value);
        if (year && year.value) terms.push(year.value);
        const q = terms.join(' ').replace(/\s+/g, ' ').trim();
        if (!q) { e.preventDefault(); (brand || qInput || form).focus && (brand || qInput).focus(); return; }
        if (qInput) qInput.value = q;
        [brand, year, engine].forEach((el) => { if (el) el.disabled = true; });
      });
    });
  });
});

/* ---------- Localization menus ---------- */

const closeLocalizationMenus = (except = null) => {
  document.querySelectorAll('.site-header__preferences details[open]').forEach((menu) => {
    if (menu !== except) menu.removeAttribute('open');
  });
};

document.addEventListener('toggle', (e) => {
  const menu = e.target.closest && e.target.closest('.site-header__preferences details[open]');
  if (menu) closeLocalizationMenus(menu);
}, true);

document.addEventListener('click', (e) => {
  if (!e.target.closest('.site-header__preferences')) closeLocalizationMenus();
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeLocalizationMenus();
});

/* ---------- ⌘K / Ctrl+K → fokus na vyhledávání ---------- */

document.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    const input = document.querySelector('.site-header__search [data-search-input], .main-search__input, [data-search-input]');
    if (input) {
      e.preventDefault();
      input.focus();
      input.select();
    }
  }
});

// Expose CartAPI for debugging / other scripts
window.P4C = { CartAPI, formatMoney, debounce, throttle, showToast };

/* ---------- Hero quick chips → vyplnit a odeslat vyhledávání ---------- */

document.addEventListener('click', (e) => {
  const chip = e.target.closest('.quick-chip[data-quick]');
  if (!chip) return;
  const form = chip.closest('form');
  const input = form?.querySelector('input[name="q"]');
  if (input) {
    input.value = chip.dataset.quick;
    form.submit();
  }
});

/* ---------- Reveal on scroll (premium vstupy sekcí) ---------- */

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;
  if (document.body.dataset.revealReady) return;
  document.body.dataset.revealReady = 'true';

  const targets = document.querySelectorAll(
    'main .section-head, main .usp-bar__item, main .cat-card, main .collection-card, main .trust-badge, main .testimonial, main .faq__item, main .reco-card, main .newsletter__inner'
  );
  if (!targets.length) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const siblings = el.parentElement ? [...el.parentElement.children].filter((c) => c.dataset.reveal === 'true') : [];
      const idx = Math.max(0, siblings.indexOf(el));
      el.style.transitionDelay = `${Math.min(idx, 5) * 70}ms`;
      el.classList.add('is-revealed');
      io.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  targets.forEach((el) => {
    el.dataset.reveal = 'true';
    io.observe(el);
  });
})();

/* ---------- Animované countery ve hero statistikách ---------- */

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const nums = document.querySelectorAll('.hero__stat-num');
  if (!nums.length || reduced || !('IntersectionObserver' in window)) return;

  const animate = (el) => {
    const raw = el.textContent.trim();
    const m = raw.match(/^([\d\s.,]+)(.*)$/);
    if (!m) return;
    const target = parseInt(m[1].replace(/[\s.,]/g, ''), 10);
    if (!target || target > 1000000) return;
    const suffix = m[2] || '';
    const sep = m[1].includes(' ') || target >= 1000;
    const dur = 1200;
    const start = performance.now();
    const fmt = (n) => (sep ? n.toLocaleString('cs-CZ').replace(/,/g, ' ') : String(n));
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(Math.round(target * eased)) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        animate(entry.target);
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.6 });

  nums.forEach((el) => io.observe(el));
})();

/* ---------- Toggle „Jen skladem" v toolbaru kolekce ---------- */

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-avail-toggle]');
  if (!btn) return;
  const input = document.querySelector('#FacetFiltersForm input[name$="availability"][value="1"]');
  if (!input) return;
  input.checked = !input.checked;
  // pamatuj si vědomou volbu „ukázat i vyprodané", ať ji default nepřepisuje
  try {
    sessionStorage.setItem('p4c-show-soldout', input.checked ? '0' : '1');
  } catch (err) { /* private mode */ }
  btn.classList.toggle('is-active', input.checked);
  btn.setAttribute('aria-pressed', String(input.checked));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------- Defaultně jen skladem (vyprodané na vyžádání) ---------- */

(() => {
  if (!document.querySelector('.main-collection')) return;
  const params = new URLSearchParams(location.search);
  for (const k of params.keys()) {
    if (k.startsWith('filter.')) return; // návštěvník už filtruje sám
  }
  let optOut = false;
  try { optOut = sessionStorage.getItem('p4c-show-soldout') === '1'; } catch (err) {}
  if (optOut) return;
  const input = document.querySelector('#FacetFiltersForm input[name$="availability"][value="1"]');
  if (!input || input.checked) return;
  input.checked = true;
  const btn = document.querySelector('[data-avail-toggle]');
  btn?.classList.add('is-active');
  btn?.setAttribute('aria-pressed', 'true');
  input.dispatchEvent(new Event('change', { bubbles: true }));
})();

/* ---------- Vyhledávání uvnitř skupiny filtrů ---------- */

document.addEventListener('input', (e) => {
  const input = e.target.closest('[data-filter-search]');
  if (!input) return;
  const content = input.closest('.filter-group__content');
  const q = input.value.trim().toLowerCase();
  let visible = 0;
  content.querySelectorAll('[data-filter-list] .tag-filter__item').forEach((li) => {
    const hit = !q || (li.dataset.filterLabel || '').includes(q);
    li.hidden = !hit;
    if (hit) visible++;
  });
  const empty = content.querySelector('[data-filter-empty]');
  if (empty) empty.hidden = visible > 0;
});

/* ---------- Hero produktový karusel ---------- */

class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dotsWrap = this.querySelector('[data-carousel-dots]');
    if (!this.track || this.slides.length === 0) return;
    this.index = 0;
    this.interval = (parseInt(this.dataset.autoplay, 10) || 5) * 1000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.dotsWrap) {
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hero-carousel__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
        dot.addEventListener('click', () => { this.goTo(i); this.restart(); });
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => { this.goTo(this.index - 1); this.restart(); });
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => { this.goTo(this.index + 1); this.restart(); });

    /* swipe na dotykových zařízeních */
    const viewport = this.querySelector('[data-carousel-viewport]');
    let startX = null;
    viewport?.addEventListener('pointerdown', (e) => { startX = e.clientX; this.stop(); }, { passive: true });
    viewport?.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 40) this.goTo(this.index + (dx < 0 ? 1 : -1));
      startX = null;
      this.start();
    }, { passive: true });

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stop() : this.start();
    });

    this.goTo(0);
    this.start();
  }

  disconnectedCallback() { this.stop(); }

  goTo(i) {
    const n = this.slides.length;
    this.index = ((i % n) + n) % n;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.slides.forEach((s, idx) => {
      s.classList.toggle('is-active', idx === this.index);
      s.setAttribute('aria-hidden', idx === this.index ? 'false' : 'true');
    });
    this.dots?.forEach((d, idx) => d.setAttribute('aria-selected', idx === this.index ? 'true' : 'false'));
  }

  start() {
    if (this.reducedMotion || this.slides.length < 2) return;
    this.stop();
    this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
  }

  stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }

  restart() { this.stop(); this.start(); }
}
customElements.define('hero-carousel', HeroCarousel);
